import { Session } from 'next-auth';
import { permanentRedirect, redirect } from 'next/navigation';

import { getProductAnimco } from '@services/animco';
import { cmsService } from '@services/cms';
import { logger } from '@services/loggers';
import { personServices } from '@services/person';
import { productDetailsService } from '@services/products/product-details';

import { PalpatineProduct } from '@app-types/api/palpatine';
import { WallType } from '@app-types/config';
import { ProductNotFoundError } from '@app-types/error';
import { ProductParentFull } from '@app-types/product';
import { SalesWallType } from '@app-types/sales';
import { UserType } from '@app-types/user';

import { PAGE_SLUG } from '@helpers/partials';
import { paths } from '@helpers/path';

import { getCurrentProduct, handleProductNotFound } from '@product-detail/helpers';

import { ProductDetail } from './product-detail';

interface AccessoriesDetailsProps {
    params: { slug: string };
    type: WallType;
    session?: Session;
}

export async function AccessoriesDetails({ params, type, session }: Readonly<AccessoriesDetailsProps>) {
    let product: ProductParentFull | undefined;

    let productStatics: PalpatineProduct[];

    const baseUrl = type === WallType.SALES_ACCESSORY ? paths.sales(SalesWallType.ACCESSORY) : paths.accessoryWall;

    try {
        [product, { items: productStatics }] = await Promise.all([
            productDetailsService.getProductDetails(type, params.slug, false, true),
            cmsService.getProduct(),
        ]);
    } catch (error) {
        if (error instanceof ProductNotFoundError) {
            logger.log('🚨 Accessory not found', params.slug);
            const redirectInfo = handleProductNotFound(params.slug, type, baseUrl);
            permanentRedirect(redirectInfo.path);
        }
        redirect(baseUrl);
    }

    const buyerContext = await personServices.getBuyerContext(session?.user as UserType | undefined);

    const { product: productWithStock, currentProduct } = await getCurrentProduct(product, params.slug, buyerContext);

    const animco = getProductAnimco(productStatics, product.urlKey, false);

    return (
        <ProductDetail
            type={type}
            baseUrl={baseUrl}
            product={productWithStock}
            currentProduct={currentProduct}
            animco={animco}
            session={session}
            enableKonami
            slug={PAGE_SLUG.ACCESSORY_PRODUCT}
            easyTradeInEnabled={false}
        />
    );
}
